import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../component/Layout";
import { useMetalPremiums } from "../hooks/useMetalPremiums";
import "./RateConstraint.css";
import {
  MetalNameCell,
  SpotPriceCell,
  ExchangeRateCell,
  PremiumInputCell,
  CalculatedRateCell,
  StateRateChips,
} from "../components/RateTableComponents";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Cell,
} from "recharts";
import {
  Lock,
  Edit2,
  History,
  RefreshCw,
  X,
  AlertTriangle,
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckSquare,
  Square,
  Shield,
  Activity,
  MapPin,
  Clock,
  Eye,
  Power,
  RotateCcw,
  Layers,
  AlertCircle,
  BarChart3,
} from "lucide-react";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const CONSTRAINT_COLLECTION = "rateConstraints";
const HISTORY_COLLECTION = "rateConstraintHistory";
const MIN_ADJ = -4;
const MAX_ADJ = 4;
const OZ_TO_GRAM = 31.103;

// Supported metals
const METALS = [
  {
    key: "gold",
    label: "Gold",
    symbol: "XAU",
    color: "#8a6b32",
    bg: "#fdf8ef",
    border: "#e8d5a3",
    accent: "#b8902e",
  },
  {
    key: "silver",
    label: "Silver",
    symbol: "XAG",
    color: "#475569",
    bg: "#f8fafc",
    border: "#cbd5e1",
    accent: "#334155",
  },
  {
    key: "platinum",
    label: "Platinum",
    symbol: "XPT",
    color: "#4f46e5",
    bg: "#eef2ff",
    border: "#c7d2fe",
    accent: "#3730a3",
  },
];

const RATE_FIELDS = [
  { key: "rtgsSale", label: "RTGS Sale" },
  { key: "rtgsPurchase", label: "RTGS Purchase" },
  { key: "metalSale", label: "Metal Sale" },
  { key: "metalPurchase", label: "Metal Purchase" },
];

// All Indian States and Union Territories
const INDIA_STATES = [
  { stateName: "Andhra Pradesh", stateCode: "AP" },
  { stateName: "Arunachal Pradesh", stateCode: "AR" },
  { stateName: "Assam", stateCode: "AS" },
  { stateName: "Bihar", stateCode: "BR" },
  { stateName: "Chhattisgarh", stateCode: "CG" },
  { stateName: "Goa", stateCode: "GA" },
  { stateName: "Gujarat", stateCode: "GJ" },
  { stateName: "Haryana", stateCode: "HR" },
  { stateName: "Himachal Pradesh", stateCode: "HP" },
  { stateName: "Jharkhand", stateCode: "JH" },
  { stateName: "Karnataka", stateCode: "KA" },
  { stateName: "Kerala", stateCode: "KL" },
  { stateName: "Madhya Pradesh", stateCode: "MP" },
  { stateName: "Maharashtra", stateCode: "MH" },
  { stateName: "Manipur", stateCode: "MN" },
  { stateName: "Meghalaya", stateCode: "ML" },
  { stateName: "Mizoram", stateCode: "MZ" },
  { stateName: "Nagaland", stateCode: "NL" },
  { stateName: "Odisha", stateCode: "OD" },
  { stateName: "Punjab", stateCode: "PB" },
  { stateName: "Rajasthan", stateCode: "RJ" },
  { stateName: "Sikkim", stateCode: "SK" },
  { stateName: "Tamil Nadu", stateCode: "TN" },
  { stateName: "Telangana", stateCode: "TS" },
  { stateName: "Tripura", stateCode: "TR" },
  { stateName: "Uttar Pradesh", stateCode: "UP" },
  { stateName: "Uttarakhand", stateCode: "UK" },
  { stateName: "West Bengal", stateCode: "WB" },
  { stateName: "Andaman & Nicobar Islands", stateCode: "AN" },
  { stateName: "Chandigarh", stateCode: "CH" },
  { stateName: "Dadra & Nagar Haveli and Daman & Diu", stateCode: "DN" },
  { stateName: "Delhi", stateCode: "DL" },
  { stateName: "Jammu & Kashmir", stateCode: "JK" },
  { stateName: "Ladakh", stateCode: "LA" },
  { stateName: "Lakshadweep", stateCode: "LD" },
  { stateName: "Puducherry", stateCode: "PY" },
];

const emptyMetalAdj = {
  rtgsSale: 0,
  rtgsPurchase: 0,
  metalSale: 0,
  metalPurchase: 0,
};
const emptyForm = {
  gold: { ...emptyMetalAdj },
  silver: { ...emptyMetalAdj },
  platinum: { ...emptyMetalAdj },
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const fmtPct = (v) => {
  const n = Number(v);
  if (isNaN(n)) return "0.00%";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const fmtRupee = (v) =>
  v != null && !isNaN(v)
    ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`
    : "—";

const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = d instanceof Date ? d : d?.toDate ? d.toDate() : new Date(d);
  return dt.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const calcRegional = (base, adj) => {
  // State Rate = Indian Base Rate + (Indian Base Rate × State Constraint%)
  if (base == null || isNaN(base)) return null;
  const adjustmentAmount = base * (Number(adj) / 100);
  return base + adjustmentAmount;
};

const getSign = (v) => {
  const n = Number(v);
  if (n > 0) return "positive";
  if (n < 0) return "negative";
  return "zero";
};

const validateAdj = (v) => {
  const n = Number(v);
  if (isNaN(n)) return "Must be a number";
  if (n < MIN_ADJ) return `Min: ${MIN_ADJ}%`;
  if (n > MAX_ADJ) return `Max: +${MAX_ADJ}%`;
  return null;
};

const isAllZero = (metalAdj) =>
  RATE_FIELDS.every((f) => Number(metalAdj?.[f.key] ?? 0) === 0);

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────
const PctBadge = ({ value }) => {
  const sign = getSign(value);
  const Icon =
    sign === "positive"
      ? TrendingUp
      : sign === "negative"
        ? TrendingDown
        : Minus;
  return (
    <span className={`rc-pct-badge rc-pct-badge--${sign}`}>
      <Icon size={10} />
      {fmtPct(value)}
    </span>
  );
};

const SummaryCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rc-card rc-summary-card">
    <div
      className="rc-summary-icon"
      style={{ background: accent + "18", color: accent }}
    >
      <Icon size={20} />
    </div>
    <div>
      <div className="rc-summary-label">{label}</div>
      <div className="rc-summary-value">{value}</div>
      {sub && <div className="rc-summary-sub">{sub}</div>}
    </div>
  </div>
);

const ConfirmDialog = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  dangerous,
}) => {
  if (!open) return null;
  return (
    <div className="rc-overlay" onClick={onCancel}>
      <div className="rc-confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="rc-confirm-icon">
          <AlertTriangle size={28} color={dangerous ? "#dc2626" : "#d97706"} />
        </div>
        <h3 className="rc-confirm-title">{title}</h3>
        <p className="rc-confirm-msg">{message}</p>
        <div className="rc-confirm-actions">
          <button className="rc-btn rc-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`rc-btn ${dangerous ? "rc-btn--danger" : "rc-btn--primary"}`}
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const PctInput = ({ label, name, value, onChange, error }) => (
  <div className="rc-form-field">
    <label className="rc-form-label">{label}</label>
    <div className="rc-pct-input-wrap">
      <input
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        step="0.01"
        min={MIN_ADJ}
        max={MAX_ADJ}
        className={`rc-pct-input ${error ? "rc-pct-input--error" : ""}`}
      />
      <span className="rc-pct-suffix">%</span>
    </div>
    {error && (
      <span className="rc-field-error">
        <AlertCircle size={11} /> {error}
      </span>
    )}
    <span className="rc-field-hint">
      {MIN_ADJ}% to +{MAX_ADJ}%
    </span>
  </div>
);

const PreviewRow = ({ label, base, adj, previousBase }) => {
  // Indian Base Rate = base
  // Current Indian Base Rate = base
  const currentIndianBaseRate = base;

  // Previous Indian Base Rate = previousBase
  const previousIndianBaseRate = previousBase || base;

  // State Rate = Indian Base Rate + (Indian Base Rate × State Constraint%)
  const currentStateRate =
    currentIndianBaseRate + currentIndianBaseRate * (Number(adj) / 100);
  const previousStateRate =
    previousIndianBaseRate + previousIndianBaseRate * (Number(adj) / 100);

  // Net Change in State Rate = Current State Rate - Previous State Rate
  const netChange = currentStateRate - previousStateRate;

  // Net Change % based on Indian Base Rate change
  // Formula: ((Current Indian Base Rate - Previous Indian Base Rate) / Previous Indian Base Rate) × 100
  const baseRateChangePercent =
    previousIndianBaseRate !== 0
      ? ((currentIndianBaseRate - previousIndianBaseRate) /
          previousIndianBaseRate) *
        100
      : 0;

  const sign = getSign(netChange);

  return (
    <div className="rc-preview-row rc-preview-row--compact">
      <span className="rc-preview-label-compact">{label}</span>
      <div className="rc-preview-chips">
        {/* Chip 1: State Asset Price (Live Rate) */}
        <div className="rc-chip rc-chip--live">
          <span className="rc-chip-value">{fmtRupee(currentStateRate)}</span>
          <span className={`rc-chip-badge ${sign}`}>
            {sign === "positive" ? "▲" : sign === "negative" ? "▼" : "●"}
          </span>
        </div>

        {/* Chip 2: State Constraint % */}
        <div className="rc-chip rc-chip--pct">
          <span className="rc-chip-pct">{fmtPct(adj)}</span>
        </div>

        {/* Chip 3: Net Price Delta (₹ change) */}
        <div className={`rc-chip rc-chip--amount rc-chip--${sign}`}>
          <span className="rc-chip-amount">
            {sign === "positive" ? "+" : ""}
            {fmtRupee(netChange)}
          </span>
        </div>

        {/* Chip 4: Net Change % based on Indian Base Rate */}
        <div className={`rc-chip rc-chip--pct rc-chip--${sign}`}>
          <span className="rc-chip-pct">
            {sign === "positive" ? "+" : ""}
            {baseRateChangePercent.toFixed(3)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// Metal adjustment editor section (used in both Edit drawer and Bulk modal)
const MetalSection = ({
  metalKey,
  metalLabel,
  metalMeta,
  formValues,
  onFieldChange,
  errors,
  baseRateForMetal,
  previousBaseRateForMetal,
}) => (
  <div className="rc-metal-section" style={{ borderColor: metalMeta.border }}>
    <div
      className="rc-metal-section-header"
      style={{ background: metalMeta.bg, borderColor: metalMeta.border }}
    >
      <span className="rc-metal-dot" style={{ background: metalMeta.color }} />
      <span
        className="rc-metal-section-label"
        style={{ color: metalMeta.color }}
      >
        {metalLabel}
      </span>
      <span className="rc-metal-section-hint">
        Base: {fmtRupee(baseRateForMetal?.rtgsSale)} (RTGS Sale) 🔄 Live
      </span>
    </div>

    {/* Preview + Inputs grouped by type */}
    <div className="rc-fields-section">
      {/* RTGS Section */}
      <div className="rc-fields-subsection">
        {baseRateForMetal && (
          <div className="rc-preview-section rc-preview-section--inline rc-preview-section--compact">
            <div className="rc-preview-label-row">
              <span className="rc-preview-title">RTGS Live Rates</span>
            </div>
            <div className="rc-preview-rows rc-preview-rows--compact">
              {RATE_FIELDS.filter((f) => f.key.startsWith("rtgs")).map((f) => (
                <PreviewRow
                  key={f.key}
                  label={f.label}
                  base={baseRateForMetal[f.key]}
                  previousBase={previousBaseRateForMetal?.[f.key]}
                  adj={formValues?.[f.key] ?? 0}
                />
              ))}
            </div>
          </div>
        )}
        <div className="rc-fields-grid rc-fields-grid--2">
          {RATE_FIELDS.filter((f) => f.key.startsWith("rtgs")).map((f) => (
            <PctInput
              key={f.key}
              label={f.label}
              name={f.key}
              value={formValues?.[f.key] ?? 0}
              onChange={(e) => onFieldChange(metalKey, f.key, e.target.value)}
              error={errors?.[f.key]}
            />
          ))}
        </div>
      </div>

      {/* Metal Section */}
      <div className="rc-fields-subsection">
        {baseRateForMetal && (
          <div className="rc-preview-section rc-preview-section--inline rc-preview-section--compact">
            <div className="rc-preview-label-row">
              <span className="rc-preview-title">Metal Live Rates</span>
            </div>
            <div className="rc-preview-rows rc-preview-rows--compact">
              {RATE_FIELDS.filter((f) => f.key.startsWith("metal")).map((f) => (
                <PreviewRow
                  key={f.key}
                  label={f.label}
                  base={baseRateForMetal[f.key]}
                  previousBase={previousBaseRateForMetal?.[f.key]}
                  adj={formValues?.[f.key] ?? 0}
                />
              ))}
            </div>
          </div>
        )}
        <div className="rc-fields-grid rc-fields-grid--2">
          {RATE_FIELDS.filter((f) => f.key.startsWith("metal")).map((f) => (
            <PctInput
              key={f.key}
              label={f.label}
              name={f.key}
              value={formValues?.[f.key] ?? 0}
              onChange={(e) => onFieldChange(metalKey, f.key, e.target.value)}
              error={errors?.[f.key]}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const CustomChartTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const sign = getSign(d.adj);
  return (
    <div className="rc-chart-tooltip">
      <div className="rc-chart-tooltip-header">
        <span className="rc-chart-tooltip-title">
          {d.stateName || d.xLabel}
        </span>
        {d.stateCode && <span className="rc-code-badge">{d.stateCode}</span>}
      </div>
      <div className="rc-chart-tooltip-body">
        <div className="rc-chart-tooltip-row">
          <span className="rc-chart-tooltip-label">Adjustment:</span>
          <span className={`rc-badge rc-badge--${sign}`}>
            {sign === "positive" ? "▲" : sign === "negative" ? "▼" : "●"}{" "}
            {fmtPct(d.adj)}
          </span>
        </div>
        {d.baseRate > 0 && (
          <>
            <div className="rc-chart-tooltip-row">
              <span className="rc-chart-tooltip-label">Indian Base Rate:</span>
              <span>{fmtRupee(d.baseRate)}</span>
            </div>
            <div className="rc-chart-tooltip-row">
              <span className="rc-chart-tooltip-label">Regional Rate:</span>
              <strong style={{ color: "#8a6b32" }}>
                {fmtRupee(d.regionalRate)}
              </strong>
            </div>
          </>
        )}
        {d.changedBy && (
          <div className="rc-chart-tooltip-footer">
            <span>By: {d.changedBy}</span>
            <span>v{d.version || 1}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
const RateConstraint = () => {
  const { user } = useAuth();

  // ── Base rates (from API) ──
  const [baseRate, setBaseRate] = useState(null); // { gold:{...}, silver:{...}, platinum:{...} }
  const [previousMinuteRate, setPreviousMinuteRate] = useState(null); // { gold:{...}, silver:{...}, platinum:{...} } from 1 min ago
  const [baseRateLoading, setBaseRateLoading] = useState(true);
  const [baseRateError, setBaseRateError] = useState("");
  const [baseRateMeta, setBaseRateMeta] = useState({
    version: "—",
    effectiveFrom: null,
    lastUpdated: null,
  });
  const [metalSpotUsd, setMetalSpotUsd] = useState(null); // { gold: number, silver: number, platinum: number }
  const [usdToInrRate, setUsdToInrRate] = useState(null); // number
  const baseRateTimer = useRef(null);

  // ── Metal premiums (synced from Firebase) ──
  const {
    premiums,
    loading: premiumsLoading,
    updatePremiums,
  } = useMetalPremiums();
  const premiumsRef = useRef(premiums);
  const [premiumDrafts, setPremiumDrafts] = useState({
    gold: 0,
    silver: 0,
    platinum: 0,
  });

  useEffect(() => {
    premiumsRef.current = premiums;
    if (premiums) {
      setPremiumDrafts({
        gold: Number(premiums.gold ?? 0),
        silver: Number(premiums.silver ?? 0),
        platinum: Number(premiums.platinum ?? 0),
      });
    }
  }, [premiums]);

  const handlePremiumChange = (metalKey, value) => {
    setPremiumDrafts((prev) => ({
      ...prev,
      [metalKey]: value,
    }));
  };

  const saveMetalPremium = async (metalKey) => {
    const value = Number(premiumDrafts[metalKey]);
    if (!Number.isFinite(value)) return;

    try {
      const nextPremiums = {
        ...(premiumsRef.current || {}),
        [metalKey]: value,
      };
      await updatePremiums(nextPremiums);
      premiumsRef.current = nextPremiums;
      setPremiumDrafts((prev) => ({ ...prev, [metalKey]: value }));
      fetchBaseRate();
    } catch (err) {
      console.error(`Failed to save ${metalKey} premium:`, err);
    }
  };

  // ── Constraints (Firestore) ──
  const [constraints, setConstraints] = useState({});
  const [constraintsLoading, setConstraintsLoading] = useState(true);
  const [constraintsError, setConstraintsError] = useState("");
  const [lastConfigUpdate, setLastConfigUpdate] = useState(null);

  // ── Table metal tab ──
  const [activeMetal, setActiveMetal] = useState("gold");

  // ── Filters ──
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [adjFilter, setAdjFilter] = useState("all");

  // ── Selection ──
  const [selected, setSelected] = useState(new Set());

  // ── Edit drawer ──
  const [editingState, setEditingState] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editErrors, setEditErrors] = useState({
    gold: {},
    silver: {},
    platinum: {},
  });
  const [editSaving, setEditSaving] = useState(false);

  // ── Bulk edit ──
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkForm, setBulkForm] = useState(emptyForm);
  const [bulkErrors, setBulkErrors] = useState({
    gold: {},
    silver: {},
    platinum: {},
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  // ── Confirm dialogs ──
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [confirmBulkEdit, setConfirmBulkEdit] = useState(false);

  // ── Audit history ──
  const [historyState, setHistoryState] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyMetal, setHistoryMetal] = useState("gold");

  // ── Graph / Analytics State ──
  const [graphTab, setGraphTab] = useState("states"); // 'states' | 'history'
  const [graphMetal, setGraphMetal] = useState("gold");
  const [graphField, setGraphField] = useState("rtgsSale");
  const [graphMetric, setGraphMetric] = useState("pct"); // 'pct' | 'rate'
  const [chartType, setChartType] = useState("bar"); // 'bar' | 'line'
  const [historyStateFilter, setHistoryStateFilter] = useState("ALL");
  const [globalHistory, setGlobalHistory] = useState([]);
  const [globalHistoryLoading, setGlobalHistoryLoading] = useState(true);

  // ─────────────────────────────────────────────
  // Fetch base rates from API (Gold + Silver + Platinum)
  // ─────────────────────────────────────────────
  const fetchBaseRate = useCallback(async () => {
    try {
      setBaseRateError("");
      setBaseRateLoading(true);

      const [goldRes, silverRes, platinumRes, fxRes] = await Promise.all([
        fetch("https://api.gold-api.com/price/XAU"),
        fetch("https://api.gold-api.com/price/XAG"),
        fetch("https://api.gold-api.com/price/XPT"),
        fetch("https://open.er-api.com/v6/latest/USD"),
      ]);
      const goldJson = goldRes.ok ? await goldRes.json() : null;
      const silverJson = silverRes.ok ? await silverRes.json() : null;
      const platJson = platinumRes.ok ? await platinumRes.json() : null;
      const fxJson = fxRes.ok ? await fxRes.json() : null;
      const usdToInr = fxJson?.rates?.INR;
      console.log(usdToInr);
      if (!usdToInr) {
        throw new Error("Could not fetch USD to INR exchange rate from API");
      }
      // Store raw metal spot prices (USD per ounce) and USD to INR rate
      setMetalSpotUsd({
        gold: goldJson?.price,
        silver: silverJson?.price,
        platinum: platJson?.price,
      });
      setUsdToInrRate(usdToInr);
      const pricePerGram = (usdPerOz, premiumPct) => {
        if (!usdPerOz) return null;
        const baseInr = (usdPerOz * usdToInr) / OZ_TO_GRAM;
        const premiumAmount = baseInr * (premiumPct / 100);
        return baseInr + premiumAmount;
      };

      const makeRates = (perGram) =>
        perGram
          ? {
              rtgsSale: perGram,
              rtgsPurchase: perGram,
              metalSale: perGram,
              metalPurchase: perGram,
            }
          : null;

      const goldPerGram = pricePerGram(
        goldJson?.price,
        premiumsRef.current.gold,
      );
      const silverPerGram = pricePerGram(
        silverJson?.price,
        premiumsRef.current.silver,
      );
      const platinumPerGram = pricePerGram(
        platJson?.price,
        premiumsRef.current.platinum,
      );
      const now = new Date();
      console.log(now);
      // Store previous rate before updating
      const prevRate = localStorage.getItem("SA_BASE_RATE_SNAPSHOT");
      if (prevRate) {
        try {
          setPreviousMinuteRate(JSON.parse(prevRate));
        } catch (e) {
          console.warn("Could not parse previous rate snapshot:", e);
        }
      }

      const newRate = {
        gold: makeRates(goldPerGram),
        silver: makeRates(silverPerGram),
        platinum: makeRates(platinumPerGram),
      };

      // Store current rate for next interval
      localStorage.setItem("SA_BASE_RATE_SNAPSHOT", JSON.stringify(newRate));

      setBaseRate(newRate);
      setBaseRateMeta({
        version: `v${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, "0")}.${String(now.getDate()).padStart(2, "0")}`,
        effectiveFrom: now,
        lastUpdated: now,
      });
    } catch (e) {
      setBaseRateError("Could not fetch live metal prices. Retrying in 60 s…");
    } finally {
      setBaseRateLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBaseRate();
    baseRateTimer.current = setInterval(fetchBaseRate, 1000);
    return () => clearInterval(baseRateTimer.current);
  }, [fetchBaseRate]);

  // ─────────────────────────────────────────────
  // Live Firestore listener for constraints
  // ─────────────────────────────────────────────
  useEffect(() => {
    setConstraintsLoading(true);
    const unsub = onSnapshot(
      collection(db, CONSTRAINT_COLLECTION),
      (snap) => {
        const map = {};
        let latestUpdate = null;
        snap.forEach((d) => {
          map[d.id] = { id: d.id, ...d.data() };
          const ua = d.data().updatedAt?.toDate?.() || null;
          if (ua && (!latestUpdate || ua > latestUpdate)) latestUpdate = ua;
        });
        setConstraints(map);
        setLastConfigUpdate(latestUpdate);
        setConstraintsLoading(false);
      },
      (err) => {
        setConstraintsError("Could not load rate constraints: " + err.message);
        setConstraintsLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // ─────────────────────────────────────────────
  // Live Firestore listener for audit history (for graph & timeline)
  // ─────────────────────────────────────────────
  useEffect(() => {
    setGlobalHistoryLoading(true);
    const q = query(
      collection(db, HISTORY_COLLECTION),
      orderBy("changedAt", "desc"),
      limit(60),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setGlobalHistory(list);
        setGlobalHistoryLoading(false);
      },
      (err) => {
        console.warn("Could not load rate constraint history for graph:", err);
        setGlobalHistoryLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // ─────────────────────────────────────────────
  // Merged state rows
  // ─────────────────────────────────────────────
  const allRows = INDIA_STATES.map((s) => {
    const c = constraints[s.stateCode];
    return {
      ...s,
      gold: { ...emptyMetalAdj, ...(c?.gold || {}) },
      silver: { ...emptyMetalAdj, ...(c?.silver || {}) },
      platinum: { ...emptyMetalAdj, ...(c?.platinum || {}) },
      active: c?.active ?? false,
      version: c?.version ?? 0,
      updatedAt: c?.updatedAt ?? null,
      configured: !!c,
    };
  });

  // Summary
  const totalStates = INDIA_STATES.length;
  const activeCount = allRows.filter((r) => r.active).length;
  const zeroCount = allRows.filter((r) =>
    METALS.every((m) => isAllZero(r[m.key])),
  ).length;

  // ── Active metal meta ──
  const activeMetalMeta = METALS.find((m) => m.key === activeMetal);
  const graphMetalMeta = METALS.find((m) => m.key === graphMetal) || METALS[0];
  const currentBaseVal = baseRate?.[graphMetal]?.[graphField] || 0;

  // ── Graph Data & Statistics ──
  const chartData = useMemo(() => {
    if (graphTab === "states") {
      return allRows.map((r) => {
        const adj = Number(r[graphMetal]?.[graphField] || 0);
        const regionalRate = calcRegional(currentBaseVal, adj);
        const val = graphMetric === "pct" ? adj : regionalRate;
        return {
          xLabel: r.stateCode,
          stateName: r.stateName,
          stateCode: r.stateCode,
          adj,
          regionalRate,
          baseRate: currentBaseVal,
          val,
          configured: r.configured,
          active: r.active,
          fillColor: adj > 0 ? "#10b981" : adj < 0 ? "#ef4444" : "#94a3b8",
        };
      });
    } else {
      // History mode
      const filtered =
        historyStateFilter === "ALL"
          ? globalHistory
          : globalHistory.filter((h) => h.stateCode === historyStateFilter);

      const sorted = [...filtered].sort((a, b) => {
        const tA = a.changedAt?.toDate ? a.changedAt.toDate().getTime() : 0;
        const tB = b.changedAt?.toDate ? b.changedAt.toDate().getTime() : 0;
        return tA - tB;
      });

      return sorted.map((h, idx) => {
        const d = h.changedAt?.toDate ? h.changedAt.toDate() : null;
        const timeStr = d
          ? `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
          : `#${idx + 1}`;
        const adj = Number(h.newValues?.[graphMetal]?.[graphField] ?? 0);
        const prevAdj = Number(
          h.previousValues?.[graphMetal]?.[graphField] ?? 0,
        );
        const regionalRate = calcRegional(currentBaseVal, adj);
        const val = graphMetric === "pct" ? adj : regionalRate;
        return {
          xLabel: `${h.stateCode} (${timeStr})`,
          shortLabel: timeStr,
          stateName: h.stateName,
          stateCode: h.stateCode,
          version: h.version,
          changedBy: h.changedBy,
          adj,
          prevAdj,
          regionalRate,
          baseRate: currentBaseVal,
          val,
          fillColor: adj > 0 ? "#10b981" : adj < 0 ? "#ef4444" : "#94a3b8",
        };
      });
    }
  }, [
    graphTab,
    allRows,
    globalHistory,
    historyStateFilter,
    graphMetal,
    graphField,
    graphMetric,
    currentBaseVal,
  ]);

  const chartStats = useMemo(() => {
    if (graphTab === "states") {
      const adjs = allRows.map((r) => Number(r[graphMetal]?.[graphField] || 0));
      const maxAdj = adjs.length ? Math.max(...adjs) : 0;
      const minAdj = adjs.length ? Math.min(...adjs) : 0;
      const avgAdj = adjs.length
        ? (adjs.reduce((a, b) => a + b, 0) / adjs.length).toFixed(3)
        : "0.000";
      const maxState = allRows.find(
        (r) => Number(r[graphMetal]?.[graphField] || 0) === maxAdj,
      );
      const minState = allRows.find(
        (r) => Number(r[graphMetal]?.[graphField] || 0) === minAdj,
      );
      return {
        maxLabel: `${maxState?.stateCode || "—"} (${maxAdj > 0 ? "+" : ""}${maxAdj}%)`,
        minLabel: `${minState?.stateCode || "—"} (${minAdj > 0 ? "+" : ""}${minAdj}%)`,
        avgLabel: `${Number(avgAdj) > 0 ? "+" : ""}${avgAdj}%`,
        count: allRows.length,
      };
    } else {
      return {
        totalLogs: globalHistory.length,
        filteredLogs: chartData.length,
        lastLog: globalHistory[0]?.changedAt
          ? fmtDateTime(globalHistory[0].changedAt)
          : "No updates yet",
      };
    }
  }, [graphTab, allRows, globalHistory, chartData, graphMetal, graphField]);

  // ── Filtered + paged ──
  const filteredRows = allRows.filter((r) => {
    const q = search.toLowerCase();
    if (
      q &&
      !r.stateName.toLowerCase().includes(q) &&
      !r.stateCode.toLowerCase().includes(q)
    )
      return false;
    if (statusFilter === "active" && !r.active) return false;
    if (statusFilter === "inactive" && r.active) return false;

    // Adj filter applies to the active metal tab
    const adj = r[activeMetal];
    if (
      adjFilter === "positive" &&
      !RATE_FIELDS.some((f) => Number(adj[f.key]) > 0)
    )
      return false;
    if (
      adjFilter === "negative" &&
      !RATE_FIELDS.some((f) => Number(adj[f.key]) < 0)
    )
      return false;
    if (adjFilter === "zero" && !isAllZero(adj)) return false;

    return true;
  });

  // ── Selection ──
  const toggleSelectAll = () =>
    setSelected(
      selected.size === filteredRows.length
        ? new Set()
        : new Set(filteredRows.map((r) => r.stateCode)),
    );
  const toggleSelect = (code) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(code) ? n.delete(code) : n.add(code);
      return n;
    });
  const allSelected =
    filteredRows.length > 0 && selected.size === filteredRows.length;

  // ─────────────────────────────────────────────
  // Validate full form (all metals)
  // ─────────────────────────────────────────────
  const validateFullForm = (form) => {
    const errs = { gold: {}, silver: {}, platinum: {} };
    let hasError = false;
    METALS.forEach((m) => {
      RATE_FIELDS.forEach((f) => {
        const e = validateAdj(form[m.key][f.key]);
        if (e) {
          errs[m.key][f.key] = e;
          hasError = true;
        }
      });
    });
    return { errs, hasError };
  };

  // ─────────────────────────────────────────────
  // Save single constraint
  // ─────────────────────────────────────────────
  const saveConstraint = async (stateCode, stateName, newMetals, isActive) => {
    const existing = constraints[stateCode];
    const prevMetals = {
      gold: existing?.gold || emptyMetalAdj,
      silver: existing?.silver || emptyMetalAdj,
      platinum: existing?.platinum || emptyMetalAdj,
    };
    const newVersion = (existing?.version ?? 0) + 1;
    const now = Timestamp.now();

    const payload = {
      stateCode,
      stateName,
      active: isActive ?? existing?.active ?? false,
      gold: newMetals.gold,
      silver: newMetals.silver,
      platinum: newMetals.platinum,
      version: newVersion,
      effectiveFrom: now,
      updatedBy: user?.mobile || "SUPERADMIN",
      updatedAt: now,
    };

    await setDoc(doc(db, CONSTRAINT_COLLECTION, stateCode), payload, {
      merge: true,
    });

    await addDoc(collection(db, HISTORY_COLLECTION), {
      stateCode,
      stateName,
      previousValues: prevMetals,
      newValues: newMetals,
      changedBy: user?.mobile || "SUPERADMIN",
      changedAt: now,
      version: newVersion,
      effectiveFrom: now,
    });
  };

  // ─────────────────────────────────────────────
  // Edit drawer
  // ─────────────────────────────────────────────
  const openEdit = (row) => {
    setEditingState(row);
    setEditForm({
      gold: { ...row.gold },
      silver: { ...row.silver },
      platinum: { ...row.platinum },
    });
    setEditErrors({ gold: {}, silver: {}, platinum: {} });
  };
  const closeEdit = () => {
    setEditingState(null);
    setEditForm(emptyForm);
    setEditErrors({ gold: {}, silver: {}, platinum: {} });
  };

  const handleEditField = (metal, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [metal]: { ...prev[metal], [field]: value },
    }));
    const e = validateAdj(value);
    setEditErrors((prev) => ({
      ...prev,
      [metal]: { ...prev[metal], [field]: e },
    }));
  };

  const handleEditSubmit = async () => {
    const { errs, hasError } = validateFullForm(editForm);
    if (hasError) {
      setEditErrors(errs);
      return;
    }
    setEditSaving(true);
    try {
      const toNum = (adj) =>
        Object.fromEntries(RATE_FIELDS.map((f) => [f.key, Number(adj[f.key])]));
      await saveConstraint(
        editingState.stateCode,
        editingState.stateName,
        {
          gold: toNum(editForm.gold),
          silver: toNum(editForm.silver),
          platinum: toNum(editForm.platinum),
        },
        editingState.active || true,
      );
      closeEdit();
    } catch (err) {
      alert("Failed to save. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Bulk reset
  // ─────────────────────────────────────────────
  const handleBulkReset = async () => {
    setConfirmReset(false);
    for (const code of [...selected]) {
      const row = allRows.find((r) => r.stateCode === code);
      if (!row) continue;
      await saveConstraint(
        code,
        row.stateName,
        { gold: emptyMetalAdj, silver: emptyMetalAdj, platinum: emptyMetalAdj },
        row.active,
      );
    }
    setSelected(new Set());
  };

  // ─────────────────────────────────────────────
  // Bulk toggle
  // ─────────────────────────────────────────────
  const handleBulkToggle = async () => {
    const isActivate = confirmToggle === "activate";
    setConfirmToggle(null);
    for (const code of [...selected]) {
      const row = allRows.find((r) => r.stateCode === code);
      if (!row) continue;
      await saveConstraint(
        code,
        row.stateName,
        { gold: row.gold, silver: row.silver, platinum: row.platinum },
        isActivate,
      );
    }
    setSelected(new Set());
  };

  // ─────────────────────────────────────────────
  // Bulk edit
  // ─────────────────────────────────────────────
  const handleBulkField = (metal, field, value) => {
    setBulkForm((prev) => ({
      ...prev,
      [metal]: { ...prev[metal], [field]: value },
    }));
    const e = validateAdj(value);
    setBulkErrors((prev) => ({
      ...prev,
      [metal]: { ...prev[metal], [field]: e },
    }));
  };

  const handleBulkEditSubmit = async () => {
    setConfirmBulkEdit(false);
    setBulkSaving(true);
    try {
      const toNum = (adj) =>
        Object.fromEntries(RATE_FIELDS.map((f) => [f.key, Number(adj[f.key])]));
      for (const code of [...selected]) {
        const row = allRows.find((r) => r.stateCode === code);
        if (!row) continue;
        await saveConstraint(
          code,
          row.stateName,
          {
            gold: toNum(bulkForm.gold),
            silver: toNum(bulkForm.silver),
            platinum: toNum(bulkForm.platinum),
          },
          row.active || true,
        );
      }
      setShowBulkEdit(false);
      setSelected(new Set());
      setBulkForm(emptyForm);
    } catch (err) {
      alert("Bulk save failed. Please try again.");
    } finally {
      setBulkSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Audit history
  // ─────────────────────────────────────────────
  const openHistory = async (stateCode) => {
    setHistoryState(stateCode);
    setHistoryMetal("gold");
    setHistoryLoading(true);
    try {
      const q = query(
        collection(db, HISTORY_COLLECTION),
        where("stateCode", "==", stateCode),
        orderBy("changedAt", "desc"),
      );
      const snap = await getDocs(q);
      setHistoryData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };
  const closeHistory = () => {
    setHistoryState(null);
    setHistoryData([]);
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <Layout pageTitle="Rate Constraints">
      <div className="rc-root">
        {/* ── Page Header ── */}
        <div className="rc-page-header">
          <div>
            <h1 className="rc-page-title">Rate Constraints</h1>
            <p className="rc-page-subtitle">
              Manage state-wise percentage adjustments (Gold · Silver ·
              Platinum) applied to the Indian Base Rate.
            </p>
          </div>
          <button
            className="rc-btn rc-btn--ghost rc-refresh-btn"
            onClick={fetchBaseRate}
            disabled={baseRateLoading}
          >
            <RefreshCw size={15} className={baseRateLoading ? "rc-spin" : ""} />
            Refresh Base Rate
          </button>
        </div>

        {/* ── Summary Cards ── */}
        <div className="rc-summary-grid">
          <SummaryCard
            icon={MapPin}
            label="Total States / UTs"
            value={totalStates}
            sub="All Indian States & UTs"
            accent="#8a6b32"
          />
          <SummaryCard
            icon={Activity}
            label="Active Configurations"
            value={activeCount}
            sub={`${totalStates - activeCount} inactive`}
            accent="#059669"
          />
          <SummaryCard
            icon={Minus}
            label="States at 0.00% (All Metals)"
            value={zeroCount}
            sub="Gold + Silver + Platinum = 0"
            accent="#4f46e5"
          />
          <SummaryCard
            icon={Clock}
            label="Last Config Update"
            value={
              lastConfigUpdate
                ? fmtDateTime(lastConfigUpdate)
                : "No updates yet"
            }
            accent="#d97706"
          />
        </div>

        {/* ── Base Rate Panel ── */}
        <div className="rc-card rc-base-rate-panel">
          <div className="rc-base-rate-header">
            <div className="rc-base-rate-title-row">
              <Lock size={16} />
              <h2 className="rc-base-rate-title">Indian Base Rate</h2>
              <span className="rc-readonly-badge">
                <Shield size={11} /> READ ONLY — Controlled by Indian Base Rate
                System
              </span>
            </div>
            <p className="rc-base-rate-subtitle">
              Live market rates (INR/gram, inclusive of India import premium).
              Live market rates are refreshed automatically. Adjustments are
              applied on top of these rates.
            </p>
          </div>

          {baseRateError && (
            <div className="rc-alert rc-alert--error">
              <AlertTriangle size={14} /> {baseRateError}
              <button className="rc-alert-retry" onClick={fetchBaseRate}>
                Retry
              </button>
            </div>
          )}

          {/* Metal Rates Table — 6 columns: Metal | Spot | USD/INR | Premium | 1g Rate | 10g Rate */}
          <div className="rc-base-rate-table rc-base-rate-table--six-col">
            <div className="rc-base-rate-table-header">
              <div className="rc-base-rate-table-cell rc-base-rate-table-cell--header">
                <span>Metal</span>
                <small>Commodity</small>
              </div>
              <div className="rc-base-rate-table-cell rc-base-rate-table-cell--header">
                <span>Spot</span>
                <small>USD / oz</small>
              </div>
              <div className="rc-base-rate-table-cell rc-base-rate-table-cell--header">
                <span>USD → INR</span>
                <small>Exchange Rate</small>
              </div>
              <div className="rc-base-rate-table-cell rc-base-rate-table-cell--header rc-base-rate-table-cell--premium">
                <span>Premium</span>
                <small>Editable %</small>
              </div>
              <div className="rc-base-rate-table-cell rc-base-rate-table-cell--header">
                <span>1g Rate</span>
                <small>₹ per gram</small>
              </div>
              <div className="rc-base-rate-table-cell rc-base-rate-table-cell--header">
                <span>10g Rate</span>
                <small>₹ per 10g</small>
              </div>
            </div>

            <div className="rc-base-rate-table-body">
              {METALS.map((metal) => {
                const spotUsd = metalSpotUsd?.[metal.key];
                const gramRate = baseRate?.[metal.key]?.rtgsSale ?? null;
                const tenGramRate =
                  gramRate != null ? Number((gramRate * 10).toFixed(2)) : null;
                const premiumValue = premiumDrafts?.[metal.key] ?? 0;

                return (
                  <div key={metal.key} className="rc-base-rate-table-row">
                    <div className="rc-base-rate-table-cell rc-base-rate-table-cell--label">
                      <MetalNameCell metal={metal} metalMeta={metal} />
                    </div>

                    <div className="rc-base-rate-table-cell">
                      <SpotPriceCell
                        spotUsd={spotUsd}
                        loading={baseRateLoading}
                      />
                    </div>

                    <div className="rc-base-rate-table-cell">
                      <ExchangeRateCell
                        usdToInr={usdToInrRate}
                        loading={baseRateLoading}
                      />
                    </div>

                    <div className="rc-base-rate-table-cell rc-base-rate-table-cell--premium">
                      <PremiumInputCell
                        metalKey={metal.key}
                        premiumValue={premiumValue}
                        loading={premiumsLoading}
                        onChange={handlePremiumChange}
                        onBlur={() => saveMetalPremium(metal.key)}
                      />
                    </div>

                    <div className="rc-base-rate-table-cell">
                      <CalculatedRateCell
                        rate={gramRate}
                        label="₹ / gram"
                        loading={baseRateLoading}
                      />
                    </div>

                    <div className="rc-base-rate-table-cell">
                      <CalculatedRateCell
                        rate={tenGramRate}
                        label="₹ / 10 gram"
                        loading={baseRateLoading}
                        isPrimary={false}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rc-base-rate-meta">
            <span>
              <strong>Version:</strong> {baseRateMeta.version}
            </span>
            <span>
              <strong>Effective From:</strong>{" "}
              {fmtDateTime(baseRateMeta.effectiveFrom)}
            </span>
            <span>
              <strong>Last Updated:</strong>{" "}
              {fmtDateTime(baseRateMeta.lastUpdated)}
            </span>
            <span className="rc-base-rate-note">
              <Eye size={12} /> Regional = Base × (Adj% ÷ 100)
            </span>
          </div>
        </div>

        {/* ── Graph Panel: All States & History (Immediately following Indian Base Rate) ── */}
        <div className="rc-card rc-chart-panel">
          <div className="rc-chart-header">
            <div className="rc-chart-title-wrap">
              <div className="rc-chart-title-row">
                <BarChart3 size={18} color={graphMetalMeta.color} />
                <h2 className="rc-chart-title">
                  {graphTab === "states"
                    ? "All States Constraint Distribution"
                    : "Rate Constraint Audit Timeline"}
                </h2>
                <span
                  className="rc-chart-tag"
                  style={{
                    color: graphMetalMeta.color,
                    borderColor: graphMetalMeta.border,
                    background: graphMetalMeta.bg,
                  }}
                >
                  {graphMetalMeta.label} ·{" "}
                  {RATE_FIELDS.find((f) => f.key === graphField)?.label}
                </span>
              </div>
              <p className="rc-chart-subtitle">
                {graphTab === "states"
                  ? "Visual distribution of percentage adjustments and regional rates across all Indian States & UTs."
                  : "Historical trace of constraint adjustments recorded across audit history updates."}
              </p>
            </div>

            {/* Mode Switcher: All States vs History */}
            <div className="rc-chart-mode-pills">
              <button
                type="button"
                className={`rc-chart-mode-pill ${graphTab === "states" ? "rc-chart-mode-pill--active" : ""}`}
                onClick={() => setGraphTab("states")}
              >
                <MapPin size={13} />
                All States ({allRows.length})
              </button>
              <button
                type="button"
                className={`rc-chart-mode-pill ${graphTab === "history" ? "rc-chart-mode-pill--active" : ""}`}
                onClick={() => setGraphTab("history")}
              >
                <History size={13} />
                Audit History ({globalHistory.length})
              </button>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="rc-chart-controls">
            {/* Metal Switcher */}
            <div className="rc-chart-control-group">
              <span className="rc-chart-control-label">Metal:</span>
              <div className="rc-chart-metal-btns">
                {METALS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={`rc-chart-metal-btn ${graphMetal === m.key ? "rc-chart-metal-btn--active" : ""}`}
                    style={
                      graphMetal === m.key
                        ? {
                            borderColor: m.color,
                            color: m.color,
                            background: m.bg,
                          }
                        : {}
                    }
                    onClick={() => setGraphMetal(m.key)}
                  >
                    <span
                      className="rc-metal-dot"
                      style={{ background: m.color }}
                    />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Field Select */}
            <div className="rc-chart-control-group">
              <span className="rc-chart-control-label">Rate Type:</span>
              <select
                className="rc-chart-select"
                value={graphField}
                onChange={(e) => setGraphField(e.target.value)}
              >
                {RATE_FIELDS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Metric Toggle: Pct vs Regional Rate */}
            <div className="rc-chart-control-group">
              <span className="rc-chart-control-label">Metric:</span>
              <div className="rc-chart-btn-group">
                <button
                  type="button"
                  className={`rc-chart-btn ${graphMetric === "pct" ? "rc-chart-btn--active" : ""}`}
                  onClick={() => setGraphMetric("pct")}
                >
                  % Adj
                </button>
                <button
                  type="button"
                  className={`rc-chart-btn ${graphMetric === "rate" ? "rc-chart-btn--active" : ""}`}
                  onClick={() => setGraphMetric("rate")}
                >
                  ₹/g Rate
                </button>
              </div>
            </div>

            {/* Chart Type: Bar vs Line */}
            <div className="rc-chart-control-group">
              <span className="rc-chart-control-label">Chart:</span>
              <div className="rc-chart-btn-group">
                <button
                  type="button"
                  className={`rc-chart-btn ${chartType === "bar" ? "rc-chart-btn--active" : ""}`}
                  onClick={() => setChartType("bar")}
                  title="Bar Chart"
                >
                  Bar
                </button>
                <button
                  type="button"
                  className={`rc-chart-btn ${chartType === "line" ? "rc-chart-btn--active" : ""}`}
                  onClick={() => setChartType("line")}
                  title="Line Chart"
                >
                  Line
                </button>
              </div>
            </div>

            {/* History State Filter (if history mode active) */}
            {graphTab === "history" && (
              <div className="rc-chart-control-group">
                <span className="rc-chart-control-label">Filter State:</span>
                <select
                  className="rc-chart-select"
                  value={historyStateFilter}
                  onChange={(e) => setHistoryStateFilter(e.target.value)}
                >
                  <option value="ALL">
                    All States ({globalHistory.length})
                  </option>
                  {INDIA_STATES.map((s) => (
                    <option key={s.stateCode} value={s.stateCode}>
                      {s.stateName} ({s.stateCode})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Chart Canvas */}
          <div className="rc-chart-canvas-wrap">
            {graphTab === "history" && globalHistoryLoading ? (
              <div className="rc-chart-empty">
                <RefreshCw size={28} className="rc-spin" color="#8a6b32" />
                <p className="rc-chart-empty-title">Loading Audit History…</p>
              </div>
            ) : graphTab === "history" && chartData.length === 0 ? (
              <div className="rc-chart-empty">
                <History size={32} color="#9ca3af" />
                <p className="rc-chart-empty-title">
                  No Audit History Found Yet
                </p>
                <span className="rc-chart-empty-sub">
                  Adjustments saved for any state will automatically generate
                  revision points and appear on this chart.
                </span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                {chartType === "bar" ? (
                  <BarChart
                    data={chartData}
                    margin={{ top: 25, right: 20, left: 10, bottom: 55 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f1f5f9"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="xLabel"
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                      tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      domain={
                        graphMetric === "pct" ? [-1, 1] : ["auto", "auto"]
                      }
                      tickFormatter={(v) =>
                        graphMetric === "pct" ? `${v}%` : `₹${v}`
                      }
                    />
                    <RechartsTooltip content={<CustomChartTooltip />} />
                    <ReferenceLine
                      y={graphMetric === "pct" ? 0 : currentBaseVal}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      label={{
                        value:
                          graphMetric === "pct"
                            ? "0.00% (Base)"
                            : `Base: ₹${currentBaseVal.toFixed(2)}`,
                        fill: "#64748b",
                        fontSize: 11,
                        position: "top",
                      }}
                    />
                    <Bar dataKey="val" radius={[3, 3, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            graphMetric === "pct"
                              ? entry.fillColor
                              : graphMetalMeta.color
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <LineChart
                    data={chartData}
                    margin={{ top: 25, right: 20, left: 10, bottom: 55 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f1f5f9"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="xLabel"
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                      tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      domain={
                        graphMetric === "pct" ? [-1, 1] : ["auto", "auto"]
                      }
                      tickFormatter={(v) =>
                        graphMetric === "pct" ? `${v}%` : `₹${v}`
                      }
                    />
                    <RechartsTooltip content={<CustomChartTooltip />} />
                    <ReferenceLine
                      y={graphMetric === "pct" ? 0 : currentBaseVal}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      label={{
                        value:
                          graphMetric === "pct"
                            ? "0.00% (Base)"
                            : `Base: ₹${currentBaseVal.toFixed(2)}`,
                        fill: "#64748b",
                        fontSize: 11,
                        position: "top",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="val"
                      stroke={graphMetalMeta.color}
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: graphMetalMeta.color }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
          </div>

          {/* Stats Bar */}
          <div className="rc-chart-stats-bar">
            {graphTab === "states" ? (
              <>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">
                    Highest Adjustment:
                  </span>
                  <strong className="rc-chart-stat-val text-green">
                    {chartStats.maxLabel}
                  </strong>
                </div>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">
                    Lowest Adjustment:
                  </span>
                  <strong className="rc-chart-stat-val text-red">
                    {chartStats.minLabel}
                  </strong>
                </div>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">
                    National Average Adj:
                  </span>
                  <strong className="rc-chart-stat-val">
                    {chartStats.avgLabel}
                  </strong>
                </div>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">Base Reference:</span>
                  <strong className="rc-chart-stat-val">
                    {fmtRupee(currentBaseVal)}
                  </strong>
                </div>
              </>
            ) : (
              <>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">
                    Total Audit History Records:
                  </span>
                  <strong className="rc-chart-stat-val">
                    {chartStats.totalLogs}
                  </strong>
                </div>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">
                    Displayed In Chart:
                  </span>
                  <strong className="rc-chart-stat-val">
                    {chartStats.filteredLogs}
                  </strong>
                </div>
                <div className="rc-chart-stat-item">
                  <span className="rc-chart-stat-label">
                    Latest Audit Recorded:
                  </span>
                  <strong className="rc-chart-stat-val">
                    {chartStats.lastLog}
                  </strong>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="rc-card rc-toolbar">
          <div className="rc-search-row">
            <div className="rc-search-wrap">
              <Search size={15} className="rc-search-icon" />
              <input
                className="rc-search-input"
                placeholder="Search state or code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="rc-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              className="rc-filter-select"
              value={adjFilter}
              onChange={(e) => setAdjFilter(e.target.value)}
            >
              <option value="all">All Adjustments</option>
              <option value="positive">Positive ▲</option>
              <option value="negative">Negative ▼</option>
              <option value="zero">Zero 0.00%</option>
            </select>
          </div>

          {selected.size > 0 && (
            <div className="rc-bulk-actions">
              <span className="rc-bulk-label">
                {selected.size} state{selected.size > 1 ? "s" : ""} selected
              </span>
              <button
                className="rc-btn rc-btn--outline"
                onClick={() => {
                  setBulkForm(emptyForm);
                  setBulkErrors({ gold: {}, silver: {}, platinum: {} });
                  setShowBulkEdit(true);
                }}
              >
                <Layers size={14} /> Bulk Edit
              </button>
              <button
                className="rc-btn rc-btn--outline rc-btn--warn"
                onClick={() => setConfirmReset(true)}
              >
                <RotateCcw size={14} /> Reset to 0.00%
              </button>
              <button
                className="rc-btn rc-btn--outline"
                onClick={() => setConfirmToggle("activate")}
              >
                <Power size={14} /> Activate
              </button>
              <button
                className="rc-btn rc-btn--outline rc-btn--danger-outline"
                onClick={() => setConfirmToggle("deactivate")}
              >
                <Power size={14} /> Deactivate
              </button>
              <button
                className="rc-btn rc-btn--ghost rc-btn--xs"
                onClick={() => setSelected(new Set())}
              >
                <X size={13} /> Clear
              </button>
            </div>
          )}
        </div>

        {/* ── State Table ── */}
        <div className="rc-card rc-table-card">
          {/* Metal tab strip above table */}
          <div className="rc-table-metal-strip">
            <span className="rc-table-showing-label">
              Showing adjustments for:
            </span>
            {METALS.map((m) => (
              <button
                key={m.key}
                className={`rc-table-metal-btn ${activeMetal === m.key ? "rc-table-metal-btn--active" : ""}`}
                style={
                  activeMetal === m.key
                    ? { background: m.color, borderColor: m.color }
                    : {}
                }
                onClick={() => setActiveMetal(m.key)}
              >
                <span
                  className="rc-metal-dot"
                  style={{
                    background: activeMetal === m.key ? "#fff" : m.color,
                  }}
                />
                {m.label}
              </button>
            ))}
          </div>

          {constraintsError && (
            <div
              className="rc-alert rc-alert--error"
              style={{ margin: "0 16px 12px" }}
            >
              <AlertTriangle size={14} /> {constraintsError}
            </div>
          )}

          <div className="rc-table-responsive">
            <table className="rc-table">
              <thead>
                <tr>
                  <th className="rc-th rc-th--check">
                    <button className="rc-check-btn" onClick={toggleSelectAll}>
                      {allSelected ? (
                        <CheckSquare size={16} color="#8a6b32" />
                      ) : (
                        <Square size={16} color="#9ca3af" />
                      )}
                    </button>
                  </th>
                  <th className="rc-th">State / UT</th>
                  <th className="rc-th rc-th--code">Code</th>
                  <th
                    className="rc-th rc-th--rate"
                    style={{ color: activeMetalMeta.color }}
                  >
                    RTGS Sale
                  </th>
                  <th
                    className="rc-th rc-th--rate"
                    style={{ color: activeMetalMeta.color }}
                  >
                    RTGS Purchase
                  </th>
                  <th
                    className="rc-th rc-th--rate"
                    style={{ color: activeMetalMeta.color }}
                  >
                    Metal Sale
                  </th>
                  <th
                    className="rc-th rc-th--rate"
                    style={{ color: activeMetalMeta.color }}
                  >
                    Metal Purchase
                  </th>
                  <th className="rc-th">Status</th>
                  <th className="rc-th rc-th--ver">Ver.</th>
                  <th className="rc-th">Last Updated</th>
                  <th className="rc-th rc-th--actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {constraintsLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="rc-tr">
                      {Array.from({ length: 11 }).map((_, j) => (
                        <td key={j} className="rc-td">
                          <span className="rc-skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="rc-empty">
                      <Search size={32} color="#d1d5db" />
                      <p>No states match your filter.</p>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const metalAdj = row[activeMetal];
                    return (
                      <tr
                        key={row.stateCode}
                        className={`rc-tr ${selected.has(row.stateCode) ? "rc-tr--selected" : ""}`}
                      >
                        <td className="rc-td rc-td--check">
                          <button
                            className="rc-check-btn"
                            onClick={() => toggleSelect(row.stateCode)}
                          >
                            {selected.has(row.stateCode) ? (
                              <CheckSquare size={15} color="#8a6b32" />
                            ) : (
                              <Square size={15} color="#d1d5db" />
                            )}
                          </button>
                        </td>
                        <td className="rc-td rc-td--state">
                          <div className="rc-state-name">{row.stateName}</div>
                          {!row.configured && (
                            <span className="rc-unconfigured-tag">
                              No config
                            </span>
                          )}
                        </td>
                        <td className="rc-td">
                          <span className="rc-code-badge">{row.stateCode}</span>
                        </td>

                        {/* RTGS Sale: State Rate Chips */}
                        <td className="rc-td rc-td--rate">
                          {baseRate &&
                          baseRate[activeMetal] &&
                          previousMinuteRate &&
                          previousMinuteRate[activeMetal] ? (
                            <StateRateChips
                              base={baseRate[activeMetal].rtgsSale}
                              previousBase={
                                previousMinuteRate[activeMetal].rtgsSale
                              }
                              adj={metalAdj.rtgsSale}
                              activeMetal={activeMetal}
                            />
                          ) : (
                            <span
                              style={{ color: "#9ca3af", fontSize: "11px" }}
                            >
                              Loading...
                            </span>
                          )}
                        </td>

                        {/* RTGS Purchase: State Rate Chips */}
                        <td className="rc-td rc-td--rate">
                          {baseRate &&
                          baseRate[activeMetal] &&
                          previousMinuteRate &&
                          previousMinuteRate[activeMetal] ? (
                            <StateRateChips
                              base={baseRate[activeMetal].rtgsPurchase}
                              previousBase={
                                previousMinuteRate[activeMetal].rtgsPurchase
                              }
                              adj={metalAdj.rtgsPurchase}
                              activeMetal={activeMetal}
                            />
                          ) : (
                            <span
                              style={{ color: "#9ca3af", fontSize: "11px" }}
                            >
                              Loading...
                            </span>
                          )}
                        </td>

                        {/* Metal Sale: State Rate Chips */}
                        <td className="rc-td rc-td--rate">
                          {baseRate &&
                          baseRate[activeMetal] &&
                          previousMinuteRate &&
                          previousMinuteRate[activeMetal] ? (
                            <StateRateChips
                              base={baseRate[activeMetal].metalSale}
                              previousBase={
                                previousMinuteRate[activeMetal].metalSale
                              }
                              adj={metalAdj.metalSale}
                              activeMetal={activeMetal}
                            />
                          ) : (
                            <span
                              style={{ color: "#9ca3af", fontSize: "11px" }}
                            >
                              Loading...
                            </span>
                          )}
                        </td>

                        {/* Metal Purchase: State Rate Chips */}
                        <td className="rc-td rc-td--rate">
                          {baseRate &&
                          baseRate[activeMetal] &&
                          previousMinuteRate &&
                          previousMinuteRate[activeMetal] ? (
                            <StateRateChips
                              base={baseRate[activeMetal].metalPurchase}
                              previousBase={
                                previousMinuteRate[activeMetal].metalPurchase
                              }
                              adj={metalAdj.metalPurchase}
                              activeMetal={activeMetal}
                            />
                          ) : (
                            <span
                              style={{ color: "#9ca3af", fontSize: "11px" }}
                            >
                              Loading...
                            </span>
                          )}
                        </td>
                        <td className="rc-td">
                          <span
                            className={`rc-status-badge ${row.active ? "rc-status--active" : "rc-status--inactive"}`}
                          >
                            {row.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="rc-td rc-td--ver">
                          {row.version > 0 ? (
                            <span className="rc-version">v{row.version}</span>
                          ) : (
                            <span className="rc-version rc-version--none">
                              —
                            </span>
                          )}
                        </td>
                        <td className="rc-td rc-td--date">
                          {fmtDateTime(row.updatedAt)}
                        </td>
                        <td className="rc-td rc-td--actions">
                          <div className="rc-action-btns">
                            <button
                              className="rc-icon-btn rc-icon-btn--edit"
                              title="Edit constraint"
                              onClick={() => openEdit(row)}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              className="rc-icon-btn rc-icon-btn--history"
                              title="Audit history"
                              onClick={() => openHistory(row.stateCode)}
                            >
                              <History size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            EDIT DRAWER
        ══════════════════════════════════════════ */}
        {editingState && (
          <div className="rc-overlay" onClick={closeEdit}>
            <div
              className="rc-drawer rc-drawer--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rc-drawer-header">
                <div>
                  <h3 className="rc-drawer-title">
                    Edit Rate Constraint — {editingState.stateName}
                  </h3>
                  <p className="rc-drawer-sub">
                    Code: <strong>{editingState.stateCode}</strong> · Ver:{" "}
                    <strong>v{editingState.version}</strong> · Range:{" "}
                    <strong>
                      {MIN_ADJ}% to +{MAX_ADJ}%
                    </strong>
                  </p>
                </div>
                <button className="rc-close-btn" onClick={closeEdit}>
                  <X size={18} />
                </button>
              </div>
              <div className="rc-drawer-body">
                <p className="rc-section-note">
                  Enter percentage adjustments only. Do <strong>not</strong>{" "}
                  enter rupee values. Each metal is adjusted independently.
                </p>
                {METALS.map((m) => (
                  <MetalSection
                    key={m.key}
                    metalKey={m.key}
                    metalLabel={m.label}
                    metalMeta={m}
                    formValues={editForm[m.key]}
                    onFieldChange={handleEditField}
                    errors={editErrors[m.key]}
                    baseRateForMetal={baseRate?.[m.key]}
                    previousBaseRateForMetal={previousMinuteRate?.[m.key]}
                  />
                ))}
              </div>
              <div className="rc-drawer-footer">
                <button className="rc-btn rc-btn--ghost" onClick={closeEdit}>
                  Cancel
                </button>
                <button
                  className="rc-btn rc-btn--primary"
                  onClick={handleEditSubmit}
                  disabled={editSaving}
                >
                  {editSaving ? "Saving…" : "Save All Constraints"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            BULK EDIT MODAL
        ══════════════════════════════════════════ */}
        {showBulkEdit && (
          <div className="rc-overlay" onClick={() => setShowBulkEdit(false)}>
            <div
              className="rc-modal rc-modal--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rc-modal-header">
                <div>
                  <h3 className="rc-drawer-title">
                    Bulk Edit — {selected.size} States
                  </h3>
                  <p className="rc-drawer-sub">
                    Same percentages will be applied to all selected states for
                    all metals.
                  </p>
                </div>
                <button
                  className="rc-close-btn"
                  onClick={() => setShowBulkEdit(false)}
                >
                  <X size={18} />
                </button>
              </div>
              <div className="rc-modal-body">
                <div className="rc-bulk-states-list">
                  <strong>Selected:</strong>{" "}
                  {[...selected].map((code) => {
                    const s = INDIA_STATES.find((x) => x.stateCode === code);
                    return (
                      <span
                        key={code}
                        className="rc-code-badge"
                        style={{ marginRight: 4 }}
                      >
                        {s?.stateName || code}
                      </span>
                    );
                  })}
                </div>
                <div style={{ marginTop: 16 }}>
                  {METALS.map((m) => (
                    <MetalSection
                      key={m.key}
                      metalKey={m.key}
                      metalLabel={m.label}
                      metalMeta={m}
                      formValues={bulkForm[m.key]}
                      onFieldChange={handleBulkField}
                      errors={bulkErrors[m.key]}
                      baseRateForMetal={baseRate?.[m.key]}
                      previousBaseRateForMetal={previousMinuteRate?.[m.key]}
                    />
                  ))}
                </div>
              </div>
              <div className="rc-modal-footer">
                <button
                  className="rc-btn rc-btn--ghost"
                  onClick={() => setShowBulkEdit(false)}
                >
                  Cancel
                </button>
                <button
                  className="rc-btn rc-btn--primary"
                  disabled={bulkSaving}
                  onClick={() => {
                    const { errs, hasError } = validateFullForm(bulkForm);
                    if (hasError) {
                      setBulkErrors(errs);
                      return;
                    }
                    setConfirmBulkEdit(true);
                  }}
                >
                  {bulkSaving ? "Saving…" : `Apply to ${selected.size} States`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            AUDIT HISTORY MODAL
        ══════════════════════════════════════════ */}
        {historyState && (
          <div className="rc-overlay" onClick={closeHistory}>
            <div
              className="rc-modal rc-modal--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rc-modal-header">
                <div>
                  <h3 className="rc-drawer-title">
                    Audit History —{" "}
                    {INDIA_STATES.find((s) => s.stateCode === historyState)
                      ?.stateName || historyState}
                  </h3>
                  <p className="rc-drawer-sub">
                    Append-only log. Historical settlement rates are never
                    altered.
                  </p>
                </div>
                <button className="rc-close-btn" onClick={closeHistory}>
                  <X size={18} />
                </button>
              </div>
              <div className="rc-modal-body">
                {/* Metal tabs in history */}
                <div className="rc-metal-tabs" style={{ marginBottom: 14 }}>
                  {METALS.map((m) => (
                    <button
                      key={m.key}
                      className={`rc-metal-tab ${historyMetal === m.key ? "rc-metal-tab--active" : ""}`}
                      style={
                        historyMetal === m.key
                          ? {
                              borderColor: m.color,
                              color: m.color,
                              background: m.bg,
                            }
                          : {}
                      }
                      onClick={() => setHistoryMetal(m.key)}
                    >
                      <span
                        className="rc-metal-dot"
                        style={{ background: m.color }}
                      />
                      {m.label}
                    </button>
                  ))}
                </div>

                {historyLoading ? (
                  <div className="rc-loading">
                    <RefreshCw size={22} className="rc-spin" /> Loading history…
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="rc-empty">
                    <History size={32} color="#d1d5db" />
                    <p>No audit history yet.</p>
                  </div>
                ) : (
                  <div className="rc-table-responsive">
                    <table className="rc-table">
                      <thead>
                        <tr>
                          <th className="rc-th">Version</th>
                          <th className="rc-th">Changed At</th>
                          <th className="rc-th">Changed By</th>
                          <th className="rc-th rc-th--num">Prev RTGS Sale</th>
                          <th className="rc-th rc-th--num">New RTGS Sale</th>
                          <th className="rc-th rc-th--num">Prev RTGS Purch.</th>
                          <th className="rc-th rc-th--num">New RTGS Purch.</th>
                          <th className="rc-th rc-th--num">Prev Metal Sale</th>
                          <th className="rc-th rc-th--num">New Metal Sale</th>
                          <th className="rc-th rc-th--num">
                            Prev Metal Purch.
                          </th>
                          <th className="rc-th rc-th--num">New Metal Purch.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyData.map((h) => {
                          const prev =
                            h.previousValues?.[historyMetal] || emptyMetalAdj;
                          const next =
                            h.newValues?.[historyMetal] || emptyMetalAdj;
                          return (
                            <tr key={h.id} className="rc-tr">
                              <td className="rc-td">
                                <span className="rc-version">v{h.version}</span>
                              </td>
                              <td className="rc-td rc-td--date">
                                {fmtDateTime(h.changedAt)}
                              </td>
                              <td className="rc-td">
                                <span className="rc-code-badge">
                                  {h.changedBy}
                                </span>
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={prev.rtgsSale} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={next.rtgsSale} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={prev.rtgsPurchase} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={next.rtgsPurchase} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={prev.metalSale} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={next.metalSale} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={prev.metalPurchase} />
                              </td>
                              <td className="rc-td rc-td--num">
                                <PctBadge value={next.metalPurchase} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="rc-modal-footer">
                <button className="rc-btn rc-btn--ghost" onClick={closeHistory}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Confirm dialogs ── */}
        <ConfirmDialog
          open={confirmReset}
          title="Reset Selected States to 0.00%?"
          message={`All Gold, Silver & Platinum adjustments will be reset to 0.00% for ${selected.size} state(s). Logged in audit history.`}
          onConfirm={handleBulkReset}
          onCancel={() => setConfirmReset(false)}
          dangerous
        />
        <ConfirmDialog
          open={!!confirmToggle}
          title={`${confirmToggle === "activate" ? "Activate" : "Deactivate"} Selected States?`}
          message={`This will ${confirmToggle} constraints for ${selected.size} state(s). Logged in audit history.`}
          onConfirm={handleBulkToggle}
          onCancel={() => setConfirmToggle(null)}
          dangerous={confirmToggle === "deactivate"}
        />
        <ConfirmDialog
          open={confirmBulkEdit}
          title="Apply Bulk Rate Constraints?"
          message={`Gold, Silver & Platinum adjustments will be applied to ${selected.size} state(s). Each change is individually logged.`}
          onConfirm={handleBulkEditSubmit}
          onCancel={() => setConfirmBulkEdit(false)}
          dangerous={false}
        />
      </div>
    </Layout>
  );
};

export default RateConstraint;
